/* ============================================================================
   INBAR+co Cart Core
   ============================================================================
   The single source of truth for all cart mutations.

   Every cart change (qty stepper, remove, cross-sell add, etc.) goes through
   ICCart.mutate(op). All mutations serialize through one queue so races are
   impossible. After each mutation completes the authoritative cart is broadcast
   via 'ic:cart:changed' so UI can re-render.

   Operations:
     {type:'change', key, quantity}
     {type:'add', id, quantity, properties}
     {type:'remove', key, isGwpDismiss}

   Events dispatched on document:
     'ic:cart:changed'  detail: {cart}   -- after every successful mutation
     'ic:cart:error'    detail: {error}  -- when a mutation fails

   Public API:
     window.ICCart.mutate(op)      returns Promise<cart>
     window.ICCart.refresh()       refetch + broadcast (no mutation)
     window.ICCart.getCart()       returns last known cart (cached)
     window.ICCart.getGWPConfig(rootEl)   read data attrs from cart container

   GWP rules (from settings, read off the cart container's data-* attrs):
     - threshold (in cents)
     - handle (gift product handle)
     - variant (gift variant id)
     - if cart total ex-GWP >= threshold AND not dismissed AND not B2B -> add GWP
     - if below threshold AND GWP in cart -> remove GWP, clear dismiss flag
     - if multiple GWP lines (race artifact) -> collapse to one
     - user manually removes GWP -> set dismiss flag for the session
   ============================================================================ */

(function () {
  'use strict';

  if (window.ICCart) return; // singleton

  const GWP_SESSION_KEY = 'ic_gwp_dismissed';
  let queue = Promise.resolve();
  let lastCart = null;

  // --------------------------------------------------------------------------
  // GWP config helper -- reads from any cart-root element with data-gwp-* attrs
  // --------------------------------------------------------------------------
  function getGWPConfig() {
    const root = document.querySelector('[data-ic-cart], [data-ic-cart-drawer]');
    if (!root) return null;
    const enabled = root.getAttribute('data-gwp-enabled') === 'true';
    const isB2b = root.getAttribute('data-gwp-b2b') === 'true';
    const variant = root.getAttribute('data-gwp-variant');
    const handle = root.getAttribute('data-gwp-handle');
    const thresholdCents = parseInt(root.getAttribute('data-gwp-threshold-cents'), 10);
    if (!enabled || isB2b || !variant || !handle || !thresholdCents) return null;
    return { variant: variant, handle: handle, thresholdCents: thresholdCents };
  }

  function isGWPDismissed() {
    try { return sessionStorage.getItem(GWP_SESSION_KEY) === '1'; }
    catch (e) { return false; }
  }
  function setGWPDismissed() {
    try { sessionStorage.setItem(GWP_SESSION_KEY, '1'); } catch (e) {}
  }
  function clearGWPDismissed() {
    try { sessionStorage.removeItem(GWP_SESSION_KEY); } catch (e) {}
  }

  // --------------------------------------------------------------------------
  // Low-level fetch helpers
  // --------------------------------------------------------------------------
  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (errData) {
          const msg = (errData && errData.description) || ('Cart request failed: ' + r.status);
          throw new Error(msg);
        }).catch(function () {
          throw new Error('Cart request failed: ' + r.status);
        });
      }
      return r.json();
    });
  }

  // --------------------------------------------------------------------------
  // Dedup -- merge any duplicate non-GWP variant lines into one.
  //
  // CRITICAL: We use sequential /cart/change.js calls, NOT bulk /cart/update.js.
  // Shopify's update.js silently no-ops when you try to merge two lines that
  // share a variant_id by setting one to 0 and another to a higher qty. The
  // change.js endpoint operates on one line at a time against a fresh cart
  // state and works reliably.
  //
  // Strategy: for each variant with duplicates, zero all but the first line
  // sequentially, then set the survivor's qty to the summed total. Because
  // each change.js call returns the updated cart, we can use that response
  // for the next call's key lookup.
  // --------------------------------------------------------------------------
  function dedupCart(cart) {
    const cfg = getGWPConfig();
    const gwpHandle = cfg ? cfg.handle : null;

    const byVariant = {};
    cart.items.forEach(function (item) {
      if (item.handle === gwpHandle) return;
      const vid = String(item.variant_id);
      if (!byVariant[vid]) byVariant[vid] = [];
      byVariant[vid].push(item);
    });

    const variantsWithDupes = Object.keys(byVariant).filter(function (vid) {
      return byVariant[vid].length > 1;
    });
    if (variantsWithDupes.length === 0) return Promise.resolve(cart);

    console.warn('IC Core: dedup needed for variants', variantsWithDupes);

    // For each variant with dupes: walk the lines sequentially.
    // First: zero every line EXCEPT the first (this collapses dupes).
    // Then: set the survivor's qty to the summed total.
    // Use reduce to chain sequentially -- never parallel.
    return variantsWithDupes.reduce(function (chain, vid) {
      return chain.then(function () {
        // Refetch to get current authoritative keys (they may have changed
        // if a prior dedup pass in this same call already ran)
        return fetchCart().then(function (currentCart) {
          const currentDupes = currentCart.items.filter(function (item) {
            return String(item.variant_id) === vid && item.handle !== gwpHandle;
          });
          if (currentDupes.length <= 1) return Promise.resolve(); // already merged

          const totalQty = currentDupes.reduce(function (s, l) { return s + l.quantity; }, 0);
          const survivor = currentDupes[0];
          const losers = currentDupes.slice(1);

          // Zero each loser sequentially
          const zeroChain = losers.reduce(function (c, line) {
            return c.then(function () {
              return postJSON('/cart/change.js', { id: line.key, quantity: 0 });
            });
          }, Promise.resolve());

          // Then set the survivor to the summed total. Refetch its key first
          // since the prior zeroings may have shifted line keys.
          return zeroChain
            .then(function () { return fetchCart(); })
            .then(function (postZeroCart) {
              const updatedSurvivor = postZeroCart.items.find(function (item) {
                return String(item.variant_id) === vid && item.handle !== gwpHandle;
              });
              if (!updatedSurvivor) return Promise.resolve();
              if (updatedSurvivor.quantity === totalQty) return Promise.resolve();
              return postJSON('/cart/change.js', {
                id: updatedSurvivor.key,
                quantity: totalQty
              });
            });
        });
      });
    }, Promise.resolve()).then(function () { return fetchCart(); });
  }

  // --------------------------------------------------------------------------
  // GWP reconciliation -- adds/removes GWP based on threshold + dismiss state
  // Also collapses any duplicate GWP lines to one (using sequential change.js
  // for the same reason as the non-GWP dedup above).
  // --------------------------------------------------------------------------
  function reconcileGWP(cart) {
    const cfg = getGWPConfig();
    if (!cfg) return Promise.resolve(cart);

    const gwpLines = cart.items.filter(function (item) {
      return item.handle === cfg.handle;
    });
    const gwpKeys = gwpLines.map(function (l) { return l.key; });

    let totalExGwp = cart.total_price;
    gwpLines.forEach(function (line) { totalExGwp -= line.final_line_price; });

    const atThreshold = totalExGwp >= cfg.thresholdCents;
    const dismissed = isGWPDismissed();

    // CASE 0: multiple GWP lines -> collapse using sequential change.js calls
    if (gwpKeys.length > 1) {
      const keepFirst = atThreshold && !dismissed;
      // Zero all GWP lines except (optionally) the first
      const toZero = keepFirst ? gwpKeys.slice(1) : gwpKeys.slice();
      return toZero.reduce(function (chain, key) {
        return chain.then(function () {
          return postJSON('/cart/change.js', { id: key, quantity: 0 });
        });
      }, Promise.resolve()).then(function () {
        // If we kept the first and it's not at qty 1, set it to qty 1
        if (keepFirst) {
          return fetchCart().then(function (postZero) {
            const survivor = postZero.items.find(function (item) {
              return item.handle === cfg.handle;
            });
            if (survivor && survivor.quantity !== 1) {
              return postJSON('/cart/change.js', { id: survivor.key, quantity: 1 });
            }
          });
        }
      });
    }

    // CASE 1: at threshold, no GWP, not dismissed -> add
    if (atThreshold && gwpKeys.length === 0 && !dismissed) {
      return postJSON('/cart/add.js', { id: cfg.variant, quantity: 1 })
        .then(function () { return fetchCart(); });
    }

    // CASE 2: below threshold, GWP present -> remove (auto, not user-driven)
    if (!atThreshold && gwpKeys.length === 1) {
      return postJSON('/cart/change.js', { id: gwpKeys[0], quantity: 0 })
        .then(function (newCart) {
          clearGWPDismissed();
          return newCart;
        });
    }

    return Promise.resolve(cart);
  }

  // --------------------------------------------------------------------------
  // Settle -- run dedup + GWP reconcile until cart is stable
  // (max 2 iterations: one for dedup, one for GWP delta caused by dedup)
  // --------------------------------------------------------------------------
  function settle(cart) {
    return dedupCart(cart)
      .then(function () { return fetchCart(); })
      .then(function (fresh) { return reconcileGWP(fresh); })
      .then(function () { return fetchCart(); });
  }

  // --------------------------------------------------------------------------
  // Execute one mutation (already serialized by queue)
  // --------------------------------------------------------------------------
  function executeMutation(op) {
    let promise;

    if (op.type === 'change') {
      promise = postJSON('/cart/change.js', { id: op.key, quantity: op.quantity });
    } else if (op.type === 'add') {
      // Smart add: increment existing matching line if present, else add new
      promise = fetchCart().then(function (cart) {
        const cfg = getGWPConfig();
        const gwpHandle = cfg ? cfg.handle : null;
        const existing = cart.items.find(function (item) {
          return String(item.variant_id) === String(op.id) && item.handle !== gwpHandle;
        });
        if (existing) {
          return postJSON('/cart/change.js', {
            id: existing.key,
            quantity: existing.quantity + (op.quantity || 1)
          });
        }
        const body = { id: op.id, quantity: op.quantity || 1 };
        if (op.properties) body.properties = op.properties;
        return postJSON('/cart/add.js', body);
      });
    } else if (op.type === 'remove') {
      if (op.isGwpDismiss) setGWPDismissed();
      promise = postJSON('/cart/change.js', { id: op.key, quantity: 0 });
    } else if (op.type === 'refresh') {
      promise = fetchCart();
    } else {
      return Promise.reject(new Error('Unknown op type: ' + op.type));
    }

    return promise
      .then(function (cart) { return settle(cart); })
      .then(function (finalCart) {
        lastCart = finalCart;
        document.dispatchEvent(new CustomEvent('ic:cart:changed', {
          detail: { cart: finalCart }
        }));
        return finalCart;
      });
  }

  // --------------------------------------------------------------------------
  // Public API -- enqueue mutations so they execute strictly in order
  // --------------------------------------------------------------------------
  function mutate(op) {
    const next = queue.then(function () {
      return executeMutation(op);
    }).catch(function (err) {
      console.error('IC Core: mutation failed', op, err);
      document.dispatchEvent(new CustomEvent('ic:cart:error', {
        detail: { error: err, op: op }
      }));
      throw err;
    });
    // Make sure queue continues even on rejection
    queue = next.catch(function () { /* swallow for chain */ });
    return next;
  }

  function refresh() {
    return mutate({ type: 'refresh' });
  }

  function getCart() {
    return lastCart;
  }

  window.ICCart = {
    mutate: mutate,
    refresh: refresh,
    getCart: getCart,
    getGWPConfig: getGWPConfig,
    setGWPDismissed: setGWPDismissed,
    clearGWPDismissed: clearGWPDismissed,
    isGWPDismissed: isGWPDismissed
  };

  // On every page load: settle the cart silently. Catches any duplicates from
  // prior sessions or third-party scripts. Broadcasts ic:cart:changed if state
  // ends up different from what was rendered server-side.
  function initialSettle() {
    fetchCart()
      .then(function (cart) {
        const initialStr = JSON.stringify(cart.items.map(function (i) {
          return { v: i.variant_id, q: i.quantity, k: i.key };
        }));
        return settle(cart).then(function (finalCart) {
          lastCart = finalCart;
          const finalStr = JSON.stringify(finalCart.items.map(function (i) {
            return { v: i.variant_id, q: i.quantity, k: i.key };
          }));
          if (initialStr !== finalStr) {
            document.dispatchEvent(new CustomEvent('ic:cart:changed', {
              detail: { cart: finalCart }
            }));
          }
        });
      })
      .catch(function (err) { console.error('IC Core: initial settle failed', err); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialSettle);
  } else {
    initialSettle();
  }
})();
